// BookTickets/stages/DateAndTime/hooks/usePersistedSelectedDate.ts
'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'

import {
  STORAGE_KEY_DATE,
  clampToTodayIfPast,
  parseQueryDate,
} from '@booking/stages/DateAndTime/lib/dateAndTime'
import { useBookingState } from '../../../state/booking.context'

type UsePersistedSelectedDateResult = {
  selectedDate: Date
  setSelectedDate: (d: Date) => void
  initialised: boolean
}

/**
 * Initialises selectedDate from:
 * 1) ?date= query param
 * 2) sessionStorage
 * 3) today
 *
 * Persists to sessionStorage whenever setSelectedDate is called via the setter returned here.
 */
export function usePersistedSelectedDate(): UsePersistedSelectedDateResult {
  const searchParams = useSearchParams()
  const { session } = useBookingState()

  const [selectedDate, setSelectedDateState] = useState<Date>(() => new Date())
  const [initialised, setInitialised] = useState(false)

  useEffect(() => {
    const fromQuery = parseQueryDate(searchParams.get('date'))

    const fromStorageRaw =
      typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY_DATE) : null
    const fromStorage = parseQueryDate(fromStorageRaw)

    console.log('session date time')
    console.log(session)

    const initial =
      fromQuery ??
      (session?.dateTime ? new Date(session.dateTime) : null) ??
      fromStorage ??
      new Date()
    const safe = clampToTodayIfPast(initial)

    setSelectedDateState(safe)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY_DATE, safe.toISOString())
    }
    setInitialised(true)
    // We intentionally only run this once on mount:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setSelectedDate = (d: Date) => {
    setSelectedDateState(d)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY_DATE, d.toISOString())
    }
  }

  return { selectedDate, setSelectedDate, initialised }
}
